import { LanguageCodes } from "@faro-lotv/flat-ui";
import { useLanguage } from "@hooks/use-language";
import { Link, LinkProps } from "@mui/material";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { EventProps } from "@faro-lotv/foreign-observers";
import { ExternalLinkEvents } from "@utils/track-event/track-event-list";

/** The available links */
export type LinkName =
  | "createNewProjectHelp"
  | "managingGroupsHelp"
  | "videoModeHelp"
  | "photos2dHelp"
  | "permissionsLevelsHelp"
  | "workspaceRolesHelp"
  | "projectRolesHelp"
  | "blinkLaunch";

/** Links to external pages used in the application */
export const AvailableLinks: {
  [key in LinkName]: (language: LanguageCodes) => string;
} = {
  /** Links to the create new project help page */
  createNewProjectHelp: (languageCode: LanguageCodes) =>
    `https://help.holobuilder.com/${languageCode}/articles/3569568-how-to-create-a-new-project-in-the-holobuilder-enterprise-dashboard`,

  /** Links to the managing groups help page */
  managingGroupsHelp: (languageCode: LanguageCodes) =>
    `https://help.holobuilder.com/${languageCode}/articles/2547401-managing-groups-in-the-enterprise-dashboard`,

  /** Links to the video mode help page */
  videoModeHelp: (languageCode: LanguageCodes) =>
    `https://help.holobuilder.com/${languageCode}/articles/6592025-how-to-capture-with-holobuilder-videomode-in-the-jobwalk-app`,

  /** Links to the 2d photos help page */
  photos2dHelp: (languageCode: LanguageCodes) =>
    `https://help.holobuilder.com/${languageCode}/articles/3548889-how-to-place-2d-photos-directly-on-your-sheet-in-the-jobwalk-app`,

  /** Link to the HoloBuilder permissions & roles help page */
  permissionsLevelsHelp: (languageCode: LanguageCodes) =>
    `https://help.holobuilder.com/${languageCode}/articles/2437877-permissions-roles-in-the-dashboard`,

  /** Link to the Sphere XG workspace roles help page */
  workspaceRolesHelp: (languageCode: LanguageCodes) =>
    `https://help.holobuilder.com/${languageCode}/articles/9626212-permission-roles-workspace-roles-in-sphere-xg`,

  /** Link to the Sphere XG project roles help page */
  projectRolesHelp: (languageCode: LanguageCodes) =>
    `https://help.holobuilder.com/${languageCode}/articles/9626328-permission-roles-project-roles-in-sphere-xg`,

  /** Link to the blink launch */
  blinkLaunch: (languageCode: LanguageCodes) => {
    // For the English language, we don't need to add the language code to the URL,
    // for the rest we use the same language code as in the app
    const languageParam = languageCode === "en" ? "" : languageCode;
    return `https://blink.faro.com/${languageParam}?utm_source=HB_SXG_PopUp&utm_medium=syndicatedcontent&utm_campaign=NPI_Blink_HoloBuilder_Backlinks&cid=701Pl00000aA4s3IAC&ls=Display%20Advertising&pint=Imaging%20Laser%20Scanner&mc=0000204&vrt=AEC&lcs=Requested%20More%20Info`;
  },
};

interface Props {
  /** The name of the link in which the component should head to */
  link: LinkName;

  /** The text to show as hyperlink */
  text?: string;

  /** Optional style to pass */
  sx?: LinkProps["sx"];

  /** Properties of the amplitude event. A link is necessary to provide so it is clear which link is selected */
  trackEventProps?: EventProps & { link: LinkName };
}

/** Navigate to provided link when user clicks on the text */
export function ExternalLinks({
  text = "Learn more",
  sx = {},
  link,
  trackEventProps,
}: Props): JSX.Element {
  const { currentLanguage } = useLanguage();
  const { trackEvent } = useTrackEvent();

  return (
    <Link
      onClick={() => {
        if (trackEventProps) {
          trackEvent({
            name: ExternalLinkEvents.openExternalLink,
            props: trackEventProps,
          });
        }
      }}
      href={AvailableLinks[link](currentLanguage.code)}
      target="_blank"
      rel="noopener noreferrer"
      sx={{ ...sx }}
    >
      {text}
    </Link>
  );
}
